import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { StepStatusEnum } from '@lib/enums/step-status.enum';
import { SignatureState, SignerStatus } from '@lib/models/generated/graphql';
import { OuiTagModule, OuiTagSize, OuiTagStatus } from 'omnium-ui/tag';

export class StatusTagDisplay {
  label: string;
  tagStatus: OuiTagStatus;
}
export type StatusEnumType = 'status' | 'document' | 'signature';

@Component({
  selector: 'lib-status-tag',
  imports: [OuiTagModule],
  templateUrl: './status-tag.component.html',
  styleUrl: './status-tag.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusTagComponent {
  status = input<StepStatusEnum | SignatureState | SignerStatus | null>();
  enumType = input<StatusEnumType>('status');
  size = input<OuiTagSize>('small');

  private statusEnumMap: Record<StepStatusEnum, { label: string; tagStatus: OuiTagStatus } | null> = {
    [StepStatusEnum.ToSign]: { label: 'À signer', tagStatus: 'todo' },
    [StepStatusEnum.Signed]: { label: 'Signé', tagStatus: 'completed' },
    [StepStatusEnum.Signing]: { label: 'En signature', tagStatus: 'transition' },
    [StepStatusEnum.ToComplete]: { label: 'À compléter', tagStatus: 'todo' },
    [StepStatusEnum.Incomplete]: { label: 'Incomplet', tagStatus: 'in-progress' },
    [StepStatusEnum.Completed]: { label: 'Complété', tagStatus: 'completed' },
    [StepStatusEnum.ToAdd]: { label: 'Manquant', tagStatus: 'error' },
    [StepStatusEnum.Added]: { label: 'Ajouté', tagStatus: 'completed' },
    [StepStatusEnum.Expired]: { label: 'Expiré', tagStatus: 'error' },
    [StepStatusEnum.Unknown]: null,
  };

  private documentStatusMap: Record<string, { label: string; tagStatus: OuiTagStatus } | null> = {
    [SignatureState.Pending]: { label: 'En cours', tagStatus: 'transition' },
    [SignatureState.Completed]: { label: 'Signé', tagStatus: 'completed' },
    [SignatureState.Expired]: { label: 'Expiré', tagStatus: 'error' },
    [SignatureState.Cancelled]: { label: 'Annulé', tagStatus: 'error' },
    [SignatureState.Failed]: { label: 'En erreur', tagStatus: 'error' },
  };

  private signatureStatusMap: Record<string, { label: string; tagStatus: OuiTagStatus } | null> = {
    [SignerStatus.Signed]: { label: 'Signé', tagStatus: 'completed' },
    [SignerStatus.Ready]: { label: 'En signature', tagStatus: 'transition' },
    [SignerStatus.CodeSent]: { label: 'En signature', tagStatus: 'transition' },
    [SignerStatus.Accessed]: { label: 'En signature', tagStatus: 'transition' },
    [SignerStatus.PendingValidation]: { label: 'En signature', tagStatus: 'transition' },
    [SignerStatus.Waiting]: { label: 'En attente', tagStatus: 'pending' },
    [SignerStatus.Canceled]: { label: 'Annulé', tagStatus: 'error' },
    [SignerStatus.Failed]: { label: 'En erreur', tagStatus: 'error' },
  };

  readonly displayedTag = computed(() => {
    const value = this.status();
    if (value === undefined) return null;

    if (this.enumType() === 'document') {
      return this.documentStatusMap[value as SignatureState] ?? null;
    } else if (this.enumType() === 'signature') {
      return this.signatureStatusMap[value as string] ?? null;
    } else {
      return this.statusEnumMap[value as StepStatusEnum] ?? null;
    }
  });
}
